import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Bluetooth,
  Check,
  CheckCircle2,
  ChevronDown,
  Cpu,
  HardDrive,
  Loader2,
  Play,
  PowerOff,
  Radio,
  RotateCcw,
  Square,
  Thermometer,
  Users,
  WifiOff,
  Zap,
} from "lucide-react";
import ConfirmDialog from "../common/ConfirmDialog";
import {
  useCaseCommand,
  useCaseQuery,
  useCaseStream,
} from "hooks/useCaseDashboard";
import {
  overallStatus,
  issueList,
  formatDuration,
  HEALTH_ISSUES,
  caseVersion,
  caseMode,
  caseModeKey,
  networkSummary,
  caseBleRange,
  isRefusedCommand,
  BLE_RANGES,
} from "utils/caseDashboard";
import { liveStats } from "utils/caseLiveStats";
import { Notice } from "./parts";
import ConnectionsCard, { viaText } from "./ConnectionsCard";
import { Sparkline, TrendLine, useNow } from "./live/shared";

export const issueLabel = (t, code) =>
  HEALTH_ISSUES.includes(code) ? t(`caseDashboard.issues.${code}`) : code;

export const modeLabel = (t, mode) => {
  const key = caseModeKey(mode);
  return key ? t(`caseDashboard.modes.${key}`) : mode;
};

const LOOK = {
  running: {
    icon: CheckCircle2,
    color: "#059669",
    grad: "linear-gradient(135deg, #ECFDF5 0%, #F0FDF4 60%, #FFFFFF 100%)",
    ring: "#A7F3D0",
  },
  attention: {
    icon: AlertTriangle,
    color: "#E07A3A",
    grad: "linear-gradient(135deg, #FFF4EA 0%, #FFF8F1 60%, #FFFFFF 100%)",
    ring: "#FDD9B5",
  },
  stopped: {
    icon: PowerOff,
    color: "#6B7280",
    grad: "linear-gradient(135deg, #F3F4F6 0%, #F9FAFB 60%, #FFFFFF 100%)",
    ring: "#E5E7EB",
  },
  unknown: {
    icon: Loader2,
    color: "#9CA3AF",
    grad: "linear-gradient(135deg, #F3F4F6 0%, #FFFFFF 100%)",
    ring: "#E5E7EB",
  },
};

/** The current session in brief, from the roster (no impact polling). */
function useSessionSummary(caseId, enabled) {
  const current = useCaseQuery(caseId, "currentSession", {}, 5000, enabled);
  const sessionId = current.data?.id || null;
  const roster = useCaseQuery(
    caseId,
    "roster",
    { sessionId },
    5000,
    enabled && !!sessionId
  );
  const now = useNow(1000, enabled);
  return useMemo(() => {
    if (!current.data) return { session: null, loading: current.loading };
    const rows = roster.data || [];
    const stats = liveStats({
      session: current.data,
      roster: rows,
      impacts: [],
      now,
    });
    const impacts = rows.reduce((n, r) => n + (Number(r.impact_count) || 0), 0);
    return { session: current.data, stats, impacts, now };
  }, [current.data, current.loading, roster.data, now]);
}

const OverviewView = ({
  caseId,
  caseRecord,
  status,
  health,
  onChanged,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const s = status.data;
  const h = health.data;
  const overall = overallStatus(s, h);
  const running = overall === "running" || overall === "attention";
  const summary = useSessionSummary(caseId, running);
  const metrics = useCaseQuery(caseId, "metrics", { hours: 1 }, 30000, running);
  // Read from the host, so they work while the app is stopped too.
  const network = useCaseCommand(caseId, "network", 30000);
  const bluetooth = useCaseCommand(caseId, "bluetooth", 10000);
  const net = networkSummary(network.data);
  const series = useMemo(() => {
    const rows = metrics.data || [];
    return {
      temp: rows.map((r) => Number(r.cpu_temp_c) || 0),
      mem: rows.map((r) => Number(r.mem_used_pct) || 0),
      heard: rows.map((r) => Number(r.wearable_matches) || 0),
    };
  }, [metrics.data]);

  const caseIssues = running ? issueList(h?.issues) : [];
  const sessionAlerts = summary.stats?.alerts || [];
  const problems =
    caseIssues.length + sessionAlerts.filter((a) => a.tone !== "info").length;
  const look = LOOK[overall];
  const Icon = look.icon;

  return (
    <div className="space-y-5">
      {/* Status banner */}
      <div
        className="relative overflow-hidden rounded-2xl border p-6"
        style={{ background: look.grad, borderColor: look.ring }}
      >
        <div className="flex flex-wrap items-center gap-5">
          <div className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)]">
            <Icon
              size={32}
              style={{ color: look.color }}
              className={overall === "unknown" ? "animate-spin" : ""}
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[28px] font-bold leading-tight text-[#111827]">
              {t(`caseDashboard.status.${overall}`)}
            </p>
            <p className="mt-0.5 text-[14px] text-[#4B5563]">
              {overall === "running" && summary.session
                ? t("caseDashboard.overview.runningSession", {
                    mode: modeLabel(t, s?.mode),
                    time: formatDuration(
                      (summary.stats?.durationMs || 0) / 1000
                    ),
                    count: summary.stats?.active.length || 0,
                  })
                : overall === "running"
                ? t("caseDashboard.overview.runningIdle", {
                    mode: modeLabel(t, s?.mode),
                  })
                : overall === "attention"
                ? t("caseDashboard.overview.attentionCount", {
                    count: Math.max(1, problems),
                  })
                : t(`caseDashboard.status.${overall}Hint`)}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-[12px] sm:grid-cols-4">
            <Meta
              label={t("caseDashboard.overview.case")}
              value={s?.hostname || caseRecord?.host}
            />
            <Meta
              label={t("caseDashboard.overview.network")}
              value={net?.ip || caseRecord?.host}
              hint={viaText(t, net)}
            />
            <Meta
              label={t("caseDashboard.overview.uptime")}
              value={s?.uptime_s == null ? "—" : formatDuration(s.uptime_s)}
            />
            <SoftwareMeta status={s} onOpen={() => onNavigate("software")} />
          </div>
        </div>
      </div>

      {status.error && !s && (
        <Notice tone="error">
          {t("caseDashboard.common.queryError", { message: status.error })}
        </Notice>
      )}

      {/* Problems, from the case and from the session, in one place */}
      {running && (caseIssues.length > 0 || sessionAlerts.length > 0) && (
        <div className="overflow-hidden rounded-xl border border-[#FDE3C8] bg-white">
          {caseIssues.map((code) => (
            <ProblemRow
              key={code}
              tone={h?.status === "critical" ? "bad" : "warn"}
              icon={Cpu}
              text={issueLabel(t, code)}
              action={t("caseDashboard.overview.seeHealth")}
              onClick={() => onNavigate("health")}
            />
          ))}
          {sessionAlerts.map((a) => (
            <ProblemRow
              key={a.id}
              tone={a.tone}
              icon={
                a.kind === "battery"
                  ? Zap
                  : a.kind === "longSession"
                  ? Activity
                  : a.kind === "quiet"
                  ? Radio
                  : WifiOff
              }
              text={t(`caseDashboard.live.alerts.${a.kind}.title`, {
                count: (a.players || []).length,
                time: a.ms != null ? formatDuration(a.ms / 1000) : "",
              })}
              detail={(a.players || []).map((p) => p.name).join(", ")}
              action={t("caseDashboard.overview.seeSession")}
              onClick={() => onNavigate("live")}
            />
          ))}
        </div>
      )}

      {/* Right now */}
      {running && (
        <div className="grid gap-4 lg:grid-cols-3">
          <NowTile
            icon={Activity}
            title={t("caseDashboard.overview.session")}
            onClick={() => onNavigate("live")}
            live={!!summary.session}
          >
            {summary.session ? (
              <>
                <div className="flex items-end gap-6">
                  <Figure
                    value={`${summary.stats.statusCounts.live}/${summary.stats.active.length}`}
                    label={t("caseDashboard.overview.playersHeard")}
                  />
                  <Figure
                    value={summary.impacts}
                    label={t("caseDashboard.live.impactsLabel")}
                  />
                  <Figure
                    value={clock(summary.stats.durationMs)}
                    label={t("caseDashboard.overview.elapsed")}
                  />
                </div>
                <PlayerDots players={summary.stats.active} />
              </>
            ) : (
              <p className="text-[13px] text-[#6B7280]">
                {summary.loading
                  ? t("caseDashboard.common.loading")
                  : t("caseDashboard.overview.noSession")}
              </p>
            )}
          </NowTile>

          <NowTile
            icon={Thermometer}
            title={t("caseDashboard.overview.health")}
            onClick={() => onNavigate("health")}
          >
            {h && h.status !== "unknown" ? (
              <>
                <div className="flex items-end gap-6">
                  <Figure
                    value={
                      h.cpu_temp_c == null
                        ? "—"
                        : `${Math.round(h.cpu_temp_c)}°`
                    }
                    label={t("caseDashboard.overview.temperature")}
                    warn={caseIssues.includes("cpu_hot")}
                  />
                  <Figure
                    value={h.mem_used_pct == null ? "—" : `${h.mem_used_pct}%`}
                    label={t("caseDashboard.overview.memory")}
                  />
                  <Figure
                    value={
                      h.disk_used_pct == null ? "—" : `${h.disk_used_pct}%`
                    }
                    label={t("caseDashboard.overview.storage")}
                    warn={caseIssues.includes("disk_full")}
                  />
                </div>
                <TrendLine
                  values={series.temp.slice(-120)}
                  color="#E07A3A"
                  height={26}
                  className="mt-3"
                />
              </>
            ) : (
              <p className="text-[13px] text-[#6B7280]">
                {t("caseDashboard.overview.noHealth")}
              </p>
            )}
          </NowTile>

          <NowTile
            icon={Radio}
            title={t("caseDashboard.overview.listening")}
            onClick={() => onNavigate("health")}
          >
            {h && h.status !== "unknown" ? (
              <>
                <div className="flex items-end gap-6">
                  <Figure
                    value={
                      h.wearable_age_s == null
                        ? "—"
                        : t("caseDashboard.overview.ago", {
                            time: formatDuration(h.wearable_age_s),
                          })
                    }
                    label={t("caseDashboard.overview.lastWearable")}
                    warn={h.wearable_age_s != null && h.wearable_age_s > 60}
                  />
                  <Figure
                    value={
                      Object.values(h.adapters || {}).filter(Boolean).length +
                      "/" +
                      Object.values(h.adapters || {}).length
                    }
                    label={t("caseDashboard.overview.radios")}
                    warn={caseIssues.some((c) => c.startsWith("adapter"))}
                  />
                  <Figure
                    value={
                      h.db_ok
                        ? t("caseDashboard.overview.dbOk")
                        : t("caseDashboard.overview.dbDown")
                    }
                    label={t("caseDashboard.overview.database")}
                    warn={!h.db_ok}
                  />
                </div>
                {series.heard.length > 1 && (
                  <Sparkline
                    values={series.heard.slice(-60)}
                    color="#10B981"
                    height={26}
                    className="mt-3"
                  />
                )}
              </>
            ) : (
              <p className="text-[13px] text-[#6B7280]">
                {t("caseDashboard.overview.noHealth")}
              </p>
            )}
          </NowTile>
        </div>
      )}

      <ConnectionsCard network={network} bluetooth={bluetooth} />

      <Controls
        caseId={caseId}
        status={s}
        health={h}
        overall={overall}
        onChanged={onChanged}
      />
    </div>
  );
};

const softwareAge = (t, date) => {
  const d = date ? new Date(date) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return t("caseDashboard.overview.updatedAgo", {
    time: formatDuration((Date.now() - d.getTime()) / 1000),
  });
};

const clock = (ms) => {
  const s = Math.max(0, Math.floor((ms || 0) / 1000));
  const h = Math.floor(s / 3600);
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return h ? `${h}:${m}:${sec}` : `${m}:${sec}`;
};

const CHANNEL_STYLE = {
  dev: "bg-[#EDE9FE] text-[#6D28D9]",
  local: "bg-[#F3F4F6] text-[#6B7280]",
};

/** The case software version, as the firmware screens show firmware. */
const SoftwareMeta = ({ status, onOpen }) => {
  const { t } = useTranslation();
  const v = caseVersion(status);
  if (!v) {
    // Software from before versioning: fall back to the commit.
    return (
      <Meta
        label={t("caseDashboard.overview.software")}
        value={status?.git?.commit ? status.git.commit.slice(0, 7) : "—"}
        title={status?.git?.subject}
        hint={softwareAge(t, status?.git?.date)}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className="min-w-0 rounded text-left transition duration-150 hover:opacity-80"
      title={[v.display, v.commit && `commit ${v.commit}`, status?.git?.subject]
        .filter(Boolean)
        .join(" · ")}
    >
      <p className="text-[11px] font-medium uppercase tracking-wide text-[#9CA3AF]">
        {t("caseDashboard.overview.software")}
      </p>
      <p className="flex items-center gap-1.5 truncate text-[13px] font-semibold text-[#111827]">
        {v.display.replace(/-dev\+\w+$/, "")}
        {v.channel && v.channel !== "production" && (
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
              CHANNEL_STYLE[v.channel] || CHANNEL_STYLE.local
            }`}
          >
            {t(`caseDashboard.overview.channel.${v.channel}`)}
          </span>
        )}
      </p>
      <p
        className={`text-[11px] ${
          v.pendingRestart ? "font-medium text-[#C2611F]" : "text-[#9CA3AF]"
        }`}
      >
        {v.pendingRestart
          ? t("caseDashboard.overview.restartToUse", {
              version: v.pendingRestart,
            })
          : v.commit
          ? t("caseDashboard.overview.commit", { commit: v.commit.slice(0, 7) })
          : softwareAge(t, status?.git?.date)}
      </p>
    </button>
  );
};

const Meta = ({ label, value, title, hint }) => (
  <div className="min-w-0" title={title || value}>
    <p className="text-[11px] font-medium uppercase tracking-wide text-[#9CA3AF]">
      {label}
    </p>
    <p className="truncate text-[13px] font-semibold text-[#111827]">
      {value || "—"}
    </p>
    {hint && <p className="text-[11px] text-[#9CA3AF]">{hint}</p>}
  </div>
);

const ProblemRow = ({ tone, icon: Icon, text, detail, action, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex w-full items-center gap-3 border-b border-[#F3F4F6] px-4 py-3 text-left transition duration-150 last:border-b-0 hover:bg-[#FFFBF7]"
  >
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
        tone === "bad"
          ? "bg-[#FEE2E2] text-[#DC2626]"
          : tone === "warn"
          ? "bg-[#FEF3E7] text-[#E07A3A]"
          : "bg-[#F3F4F6] text-[#6B7280]"
      }`}
    >
      <Icon size={16} />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-[13px] font-semibold text-[#111827]">
        {text}
      </span>
      {detail && (
        <span className="block truncate text-[12px] text-[#6B7280]">
          {detail}
        </span>
      )}
    </span>
    <span className="flex items-center gap-1 text-[12px] font-medium text-[#C2611F] opacity-70 transition group-hover:opacity-100">
      {action}
      <ArrowRight size={13} />
    </span>
  </button>
);

const NowTile = ({ icon: Icon, title, onClick, live, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="group rounded-xl border border-[#F3F4F6] bg-white p-4 text-left transition duration-150 hover:border-[#E5E7EB] hover:shadow-[0_4px_16px_rgba(0,0,0,0.05)]"
  >
    <div className="mb-3 flex items-center justify-between">
      <span className="flex items-center gap-2 text-[13px] font-semibold text-[#111827]">
        <Icon size={15} className="text-[#9CA3AF]" />
        {title}
        {live && (
          <span className="relative ml-1 flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#16A34A] opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#16A34A]" />
          </span>
        )}
      </span>
      <ArrowRight
        size={14}
        className="text-[#D1D5DB] transition group-hover:translate-x-0.5 group-hover:text-[#9CA3AF]"
      />
    </div>
    {children}
  </button>
);

const Figure = ({ value, label, warn }) => (
  <div className="min-w-0">
    <p
      className={`text-[22px] font-bold tabular-nums leading-none ${
        warn ? "text-[#E07A3A]" : "text-[#111827]"
      }`}
    >
      {value}
    </p>
    <p className="mt-1 text-[11px] text-[#6B7280]">{label}</p>
  </div>
);

const DOT_COLOR = {
  live: "#16A34A",
  quiet: "#E07A3A",
  silent: "#DC2626",
  never: "#D1D5DB",
};

/** A dot per player, coloured by whether the case can hear them. */
const PlayerDots = ({ players }) => (
  <div className="mt-3 flex flex-wrap gap-1">
    {players.map((p) => (
      <span
        key={p.key}
        title={p.name}
        className="h-2.5 w-2.5 rounded-full"
        style={{ backgroundColor: DOT_COLOR[p.status] || "#D1D5DB" }}
      />
    ))}
  </div>
);

// ── Controls ────────────────────────────────────────────────────────────────

const STEPS = [
  { match: /down/, key: "stopping" },
  { match: /build/, key: "building" },
  { match: /up -d|force-recreate/, key: "starting" },
];

const MODE_ICON = {
  receiver: Radio,
  "pairing-only": Users,
  offload: HardDrive,
};

const Controls = ({ caseId, status, health, overall, onChanged }) => {
  const { t } = useTranslation();
  const stream = useCaseStream(caseId);
  const [confirm, setConfirm] = useState(null);
  const [showLines, setShowLines] = useState(false);
  const [last, setLast] = useState(null);
  const [current, setCurrent] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const now = useNow(1000, stream.running);
  const logRef = useRef(null);
  const modes = status?.modes || [];
  const isRunning = overall === "running" || overall === "attention";

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [stream.lines]);

  const run = async (command) => {
    setConfirm(null);
    setLast(null);
    setCurrent(command);
    setStartedAt(Date.now());
    const result = await stream.start(command);
    setLast({ command, result });
    onChanged();
  };

  const ask = (command, titleKey, bodyKey, vars = {}) =>
    setConfirm({
      command,
      title: t(`caseDashboard.controls.${titleKey}`, vars),
      body: t(`caseDashboard.controls.${bodyKey}`),
      danger: command !== "start",
    });

  // Which steps of this action we've reached, from the "> docker-compose …" lines.
  const plan =
    current === "restart"
      ? STEPS
      : current === "stop"
      ? STEPS.slice(0, 1)
      : current
      ? STEPS.slice(2)
      : [];
  const reached = plan.map((step) =>
    stream.lines.some((l) => l.startsWith(">") && step.match.test(l))
  );
  const activeStep = reached.lastIndexOf(true);

  return (
    <div className="rounded-xl border border-[#F3F4F6] bg-white">
      <div className="border-b border-[#F3F4F6] px-4 py-3">
        <h2 className="text-sm font-semibold text-[#111827]">
          {t("caseDashboard.controls.title")}
        </h2>
      </div>
      <div className="grid gap-3 p-4 md:grid-cols-2">
        <ActionCard
          icon={RotateCcw}
          title={t("caseDashboard.controls.restartCard")}
          body={t("caseDashboard.controls.restartCardBody")}
          disabled={stream.running}
          onClick={() => ask("restart", "restartTitle", "restartBody")}
          accent
        />
        {isRunning ? (
          <ActionCard
            icon={Square}
            title={t("caseDashboard.controls.stopCard")}
            body={t("caseDashboard.controls.stopCardBody")}
            disabled={stream.running}
            onClick={() => ask("stop", "stopTitle", "stopBody")}
          />
        ) : (
          <ActionCard
            icon={Play}
            title={t("caseDashboard.controls.startCard")}
            body={t("caseDashboard.controls.startCardBody")}
            disabled={stream.running}
            onClick={() => ask("start", "startTitle", "startBody")}
            accent
          />
        )}
      </div>

      {(stream.running || last) && (
        <div className="mx-4 mb-4 rounded-xl border border-[#F3F4F6] bg-[#F9FAFB] p-4">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2 text-[13px] font-semibold text-[#111827]">
              {stream.running ? (
                <Loader2 size={15} className="animate-spin text-[#E07A3A]" />
              ) : last?.result?.ok ? (
                <CheckCircle2 size={15} className="text-[#059669]" />
              ) : (
                <AlertTriangle size={15} className="text-[#DC2626]" />
              )}
              {stream.running
                ? t(
                    `caseDashboard.controls.doing.${
                      current?.split(" ")[0] || "start"
                    }`
                  )
                : last?.result?.ok
                ? t("caseDashboard.controls.done")
                : t("caseDashboard.controls.failed", {
                    message: last?.result?.error || "",
                  })}
            </p>
            {stream.running && startedAt && (
              <span className="font-mono text-[12px] tabular-nums text-[#6B7280]">
                {clock(now - startedAt)}
              </span>
            )}
          </div>
          {plan.length > 1 && (
            <ol className="mt-3 flex items-center gap-2">
              {plan.map((step, i) => {
                const done = !stream.running
                  ? last?.result?.ok
                  : i < activeStep;
                const active = stream.running && i === activeStep;
                return (
                  <li key={step.key} className="flex flex-1 items-center gap-2">
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                        done
                          ? "bg-[#059669] text-white"
                          : active
                          ? "bg-[#E07A3A] text-white"
                          : "bg-[#E5E7EB] text-[#9CA3AF]"
                      }`}
                    >
                      {done ? (
                        <Check size={13} />
                      ) : active ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        i + 1
                      )}
                    </span>
                    <span
                      className={`text-[12px] font-medium ${
                        active ? "text-[#111827]" : "text-[#6B7280]"
                      }`}
                    >
                      {t(`caseDashboard.controls.steps.${step.key}`)}
                    </span>
                    {i < plan.length - 1 && (
                      <span className="h-px flex-1 bg-[#E5E7EB]" />
                    )}
                  </li>
                );
              })}
            </ol>
          )}
          {stream.running &&
            (current === "restart" || current?.startsWith("mode")) && (
              <p className="mt-3 text-[12px] text-[#6B7280]">
                {t("caseDashboard.controls.restartHint")}
              </p>
            )}
          {stream.lines.length > 0 && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowLines((v) => !v)}
                className="flex items-center gap-1 text-xs font-medium text-[#6B7280] hover:text-[#374151]"
              >
                <ChevronDown
                  size={14}
                  className={`transition duration-150 ${
                    showLines ? "rotate-180" : ""
                  }`}
                />
                {showLines
                  ? t("caseDashboard.controls.hideProgress")
                  : t("caseDashboard.controls.showProgress")}
              </button>
              {showLines && (
                <pre
                  ref={logRef}
                  className="mt-2 max-h-56 overflow-auto rounded-lg bg-[#111827] p-3 font-mono text-[11px] leading-relaxed text-[#E5E7EB]"
                >
                  {stream.lines
                    .filter((l) => !l.startsWith("@@JSON "))
                    .join("\n")}
                </pre>
              )}
            </div>
          )}
        </div>
      )}

      {modes.length > 0 && (
        <div className="border-t border-[#F3F4F6] p-4">
          <p className="mb-1 text-[13px] font-semibold text-[#111827]">
            {t("caseDashboard.controls.modeHeading")}
          </p>
          <p className="mb-3 text-[12px] text-[#6B7280]">
            {t("caseDashboard.controls.modeIntro")}
          </p>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {modes.map((m) => {
              // m is the case's own name for the mode (old or new); the
              // command sends it back as is.
              const MIcon = MODE_ICON[caseMode(m)] || Activity;
              const key = caseModeKey(m);
              const active = caseMode(m) === caseMode(status?.mode);
              return (
                <button
                  type="button"
                  key={m}
                  disabled={stream.running || active}
                  onClick={() =>
                    ask(`mode ${m}`, "modeTitle", "modeBody", {
                      mode: modeLabel(t, m),
                    })
                  }
                  className={`relative rounded-xl border p-3 text-left transition duration-150 disabled:cursor-default ${
                    active
                      ? "border-[#E07A3A] bg-[#FFF8F1]"
                      : "border-[#F3F4F6] hover:border-[#E5E7EB] hover:bg-[#FAFAFA] disabled:opacity-60"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <MIcon
                      size={15}
                      className={active ? "text-[#E07A3A]" : "text-[#9CA3AF]"}
                    />
                    <span className="text-[13px] font-semibold text-[#111827]">
                      {key ? t(`caseDashboard.modes.short.${key}`) : m}
                    </span>
                    {active && (
                      <span className="ml-auto rounded-full bg-[#E07A3A] px-1.5 py-0.5 text-[10px] font-semibold text-white">
                        {t("caseDashboard.controls.current")}
                      </span>
                    )}
                  </span>
                  <span className="mt-1 block text-[12px] leading-snug text-[#6B7280]">
                    {key ? t(`caseDashboard.modes.about.${key}`) : m}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {status && (
        <RangeSwitch
          caseId={caseId}
          status={status}
          health={health}
          onChanged={onChanged}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={t("caseDashboard.controls.confirm")}
        cancelLabel={t("caseDashboard.controls.cancel")}
        danger={confirm?.danger}
        onConfirm={() => run(confirm.command)}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

const rangeLabel = (t, range) =>
  BLE_RANGES.includes(range) ? t(`caseDashboard.range.${range}.short`) : range;

/**
 * The Bluetooth range the case scans and pairs on (case software v0.9.0+).
 * No restart: the case restarts its scan within a second. Only the receiver
 * decides it; in pairing-only mode the other receiver does.
 */
const RangeSwitch = ({ caseId, status, health, onChanged }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(null);
  const [last, setLast] = useState(null);
  const range = caseBleRange(status, health);
  const pairingOnly = caseMode(status?.mode) === "pairing-only";

  const choose = async (next) => {
    setBusy(next);
    setLast(null);
    const res = await window.electronAPI?.caseDashboard?.run(
      caseId,
      `ble-range ${next}`
    );
    setBusy(null);
    const json = res?.json;
    if (res?.ok && json?.ok !== false) {
      setLast({ ok: true, setting: json?.setting || next });
    } else {
      const error = json?.error || res?.error?.message || res?.error || "";
      setLast({
        ok: false,
        error: isRefusedCommand(error)
          ? t("caseDashboard.common.needsVersion", { version: "v0.9.0" })
          : error,
      });
    }
    onChanged();
  };

  return (
    <div className="border-t border-[#F3F4F6] p-4">
      <p className="mb-1 flex items-center gap-1.5 text-[13px] font-semibold text-[#111827]">
        <Bluetooth size={14} className="text-[#9CA3AF]" />
        {t("caseDashboard.range.heading")}
      </p>
      {!range ? (
        <p className="text-[12px] text-[#6B7280]">
          {t("caseDashboard.common.needsVersion", { version: "v0.9.0" })}
        </p>
      ) : (
        <>
          <p className="mb-3 text-[12px] text-[#6B7280]">
            {pairingOnly
              ? t("caseDashboard.range.pairingOnly")
              : t("caseDashboard.range.intro")}
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {BLE_RANGES.map((r) => {
              const active = r === range.setting;
              return (
                <button
                  type="button"
                  key={r}
                  disabled={pairingOnly || !!busy || active}
                  onClick={() => choose(r)}
                  className={`relative rounded-xl border p-3 text-left transition duration-150 disabled:cursor-default ${
                    active
                      ? `border-[#E07A3A] bg-[#FFF8F1] ${
                          pairingOnly ? "opacity-60" : ""
                        }`
                      : "border-[#F3F4F6] hover:border-[#E5E7EB] hover:bg-[#FAFAFA] disabled:opacity-60"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {busy === r && (
                      <Loader2
                        size={14}
                        className="animate-spin text-[#E07A3A]"
                      />
                    )}
                    <span className="text-[13px] font-semibold text-[#111827]">
                      {t(`caseDashboard.range.${r}.title`)}
                    </span>
                    {r === "coded" && !active && (
                      <span className="rounded-full bg-[#F3F4F6] px-1.5 py-0.5 text-[10px] font-semibold text-[#6B7280]">
                        {t("caseDashboard.range.recommended")}
                      </span>
                    )}
                    {active && (
                      <span className="ml-auto rounded-full bg-[#E07A3A] px-1.5 py-0.5 text-[10px] font-semibold text-white">
                        {t("caseDashboard.controls.current")}
                      </span>
                    )}
                  </span>
                  <span className="mt-1 block text-[12px] leading-snug text-[#6B7280]">
                    {t(`caseDashboard.range.${r}.about`)}
                  </span>
                </button>
              );
            })}
          </div>
          {!pairingOnly && range.listening !== range.setting && (
            <p className="mt-2 text-[12px] text-[#6B7280]">
              {range.listening
                ? t("caseDashboard.range.listeningOther", {
                    range: rangeLabel(t, range.listening),
                  })
                : t("caseDashboard.range.notListening")}
            </p>
          )}
          {last && (
            <div className="mt-3">
              <Notice tone={last.ok ? "info" : "error"}>
                {last.ok
                  ? t("caseDashboard.range.changed", {
                      range: rangeLabel(t, last.setting),
                    })
                  : t("caseDashboard.controls.failed", {
                      message: last.error,
                    })}
              </Notice>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ActionCard = ({ icon: Icon, title, body, onClick, disabled, accent }) => (
  <motion.button
    type="button"
    whileHover={disabled ? undefined : { y: -1 }}
    onClick={onClick}
    disabled={disabled}
    className="flex items-start gap-3 rounded-xl border border-[#F3F4F6] p-4 text-left transition duration-150 hover:border-[#E5E7EB] hover:shadow-[0_4px_16px_rgba(0,0,0,0.05)] disabled:cursor-not-allowed disabled:opacity-50"
  >
    <span
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
        accent ? "bg-[#FEF3E7] text-[#E07A3A]" : "bg-[#F3F4F6] text-[#6B7280]"
      }`}
    >
      <Icon size={18} />
    </span>
    <span>
      <span className="block text-[14px] font-semibold text-[#111827]">
        {title}
      </span>
      <span className="mt-0.5 block text-[12px] leading-snug text-[#6B7280]">
        {body}
      </span>
    </span>
  </motion.button>
);

export default OverviewView;
